import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Minus, Send, Package, Users, Bell } from 'lucide-react';

function Trend({ value, suffix = '%' }) {
  if (value === 0 || value === null || value === undefined) return <span className="inline-flex items-center gap-1 text-[#3D2817]/50 text-xs"><Minus className="w-3 h-3" /> 0{suffix}</span>;
  const up = value > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${up ? 'text-green-700' : 'text-red-700'}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? '+' : ''}{value}{suffix}
    </span>
  );
}

function StatCard({ label, value, sub, trend, icon: Icon, testid, big = false }) {
  return (
    <div data-testid={testid} className="border border-[#E5D9C8] rounded-sm p-6 bg-white">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-[#3D2817]/50">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-[#C5A059]" />}
      </div>
      <div className={`font-serif ${big ? 'text-5xl' : 'text-4xl'}`}>{value}</div>
      <div className="mt-2 flex items-center gap-3 text-xs text-[#3D2817]/60">
        {sub && <span>{sub}</span>}
        {trend != null && <Trend value={trend} />}
      </div>
    </div>
  );
}

export default function KpiTab({ onNavigate }) {
  const [kpi, setKpi] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => api.get('/admin/kpi').then(r => setKpi(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runFollowups = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/run-followups');
      toast.success(`Follow-ups: ${data.sent} sent, ${data.skipped} skipped`);
      load();
    } catch { toast.error('Failed'); }
    finally { setRunning(false); }
  };

  if (!kpi) return <div className="text-sm text-[#3D2817]/50">Loading…</div>;

  return (
    <div>
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-2">— Dashboard</div>
          <h2 className="font-serif text-4xl">Overview</h2>
          <p className="text-sm text-[#3D2817]/60 mt-1">Last 30 days vs previous 30 days.</p>
        </div>
        {kpi.followups.pending > 0 && (
          <Button data-testid="run-followups" onClick={runFollowups} disabled={running}
            className="bg-[#C5A059] hover:bg-[#A88544] text-white">
            <Send className="w-4 h-4 mr-2" />
            {running ? 'Sending…' : `Send ${kpi.followups.pending} pending follow-ups`}
          </Button>
        )}
      </div>

      {/* Primary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          testid="kpi-quotes"
          label="Quote Requests"
          value={kpi.quotes.current}
          sub={`vs ${kpi.quotes.previous} previous`}
          trend={kpi.quotes.delta_pct}
          icon={Send}
          big
        />
        <StatCard
          testid="kpi-conversion"
          label="Conversion"
          value={`${kpi.conversion.current_pct}%`}
          sub={`vs ${kpi.conversion.previous_pct}%`}
          trend={kpi.conversion.delta_pct_points}
        />
        <StatCard
          testid="kpi-pipeline"
          label="Closed Pipeline"
          value={`${kpi.pipeline.value.toLocaleString()} ${kpi.pipeline.currency}`}
          sub={`${kpi.pipeline.matched_items} priced items`}
        />
        <StatCard
          testid="kpi-followups"
          label="Follow-ups Pending"
          value={kpi.followups.pending}
          sub={`${kpi.followups.sent_30d} sent (30d)`}
          icon={Bell}
        />
      </div>

      {/* Secondary row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Growing countries */}
        <div data-testid="kpi-countries" className="border border-[#E5D9C8] rounded-sm p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg">Growing Markets</h3>
            <TrendingUp className="w-4 h-4 text-[#C5A059]" />
          </div>
          {kpi.growing_countries.length === 0 && <div className="text-sm text-[#3D2817]/50">No data yet.</div>}
          <ul className="space-y-3">
            {kpi.growing_countries.map(c => (
              <li key={c.country} className="flex items-center justify-between">
                <div>
                  <div className="font-serif">{c.country}</div>
                  <div className="text-xs text-[#3D2817]/50">{c.previous} → {c.current}</div>
                </div>
                <Badge className={c.delta > 0 ? 'bg-green-100 text-green-800' : c.delta < 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}>
                  {c.delta > 0 ? '+' : ''}{c.delta}
                </Badge>
              </li>
            ))}
          </ul>
        </div>

        {/* Status breakdown */}
        <div className="border border-[#E5D9C8] rounded-sm p-6 bg-white">
          <h3 className="font-serif text-lg mb-4">Quote Status — 30d</h3>
          {[
            { key: 'new', label: 'New', color: 'bg-blue-500' },
            { key: 'in_progress', label: 'In progress', color: 'bg-yellow-500' },
            { key: 'closed', label: 'Closed', color: 'bg-green-600' },
          ].map(s => {
            const n = kpi.quotes.by_status[s.key] || 0;
            const total = kpi.quotes.current || 1;
            return (
              <div key={s.key} className="mb-3">
                <div className="flex justify-between text-sm mb-1">
                  <span>{s.label}</span>
                  <span className="font-serif">{n}</span>
                </div>
                <div className="h-2 bg-[#F2ECE0] rounded overflow-hidden">
                  <div className={`h-full ${s.color}`} style={{ width: `${(n/total)*100}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Catalog snapshot */}
        <div className="border border-[#E5D9C8] rounded-sm p-6 bg-white">
          <h3 className="font-serif text-lg mb-4">Catalog</h3>
          <div className="space-y-4">
            <button data-testid="kpi-goto-products" onClick={() => onNavigate?.('products')} className="w-full flex items-center justify-between hover:bg-[#F2ECE0]/50 -mx-2 px-2 py-2 transition-colors">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-[#C5A059]" />
                <span>Active Products</span>
              </div>
              <span className="font-serif text-2xl">{kpi.catalog.active_products}</span>
            </button>
            <button data-testid="kpi-goto-buyers" onClick={() => onNavigate?.('buyers')} className="w-full flex items-center justify-between hover:bg-[#F2ECE0]/50 -mx-2 px-2 py-2 transition-colors">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#C5A059]" />
                <span>Registered Buyers</span>
              </div>
              <span className="font-serif text-2xl">{kpi.catalog.buyers}</span>
            </button>
          </div>
          <div className="mt-6 pt-4 border-t border-[#E5D9C8] text-xs text-[#3D2817]/50">
            <p>{kpi.pipeline.note}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
